import { Component, DestroyRef, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subscription, interval } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CardsAdminService,
  PublishOverrides,
  QuoteSuggestion,
  StudioRun
} from '../../../services/admin/cards-admin.service';
import { ModalComponent, ModalButton } from '../../../common-components/modal/modal.component';

type StudioView = 'form' | 'generating' | 'review' | 'published';

@Component({
  selector: 'app-cards-studio-tab',
  imports: [TranslocoModule, ReactiveFormsModule, ModalComponent],
  templateUrl: './cards-studio-tab.component.html',
  styleUrl: './cards-studio-tab.component.scss'
})
export class CardsStudioTabComponent implements OnInit {
  view = signal<StudioView>('form');
  run = signal<StudioRun | null>(null);
  // Specific errors render inline (calendar-tab idiom); the errors interceptor
  // additionally snackbars generalErrorText on its own - that's the house norm.
  error = signal<string | null>(null); // i18n key
  starting = signal(false);

  nameEnControl = new FormControl<string>('', { nonNullable: true });
  nameUkControl = new FormControl<string>('', { nonNullable: true });
  quoteEnControl = new FormControl<string>('', { nonNullable: true });
  quoteUkControl = new FormControl<string>('', { nonNullable: true });
  descriptionControl = new FormControl<string>('', { nonNullable: true });
  hintControl = new FormControl<string>('', { nonNullable: true });
  rarityControl = new FormControl<string>('common', { nonNullable: true });

  selectedCandidateId = signal<number | null>(null);
  suggestions = signal<QuoteSuggestion[]>([]);
  suggesting = signal(false);
  stopping = signal(false);
  showPublishModal = signal(false);
  publishing = signal(false);
  // Publish failures are modal-scoped: a separate signal keeps them from also
  // rendering in the review view behind the overlay, and open/close reset it
  // so a dismissed modal never leaves its message stuck on screen.
  publishError = signal<string | null>(null); // i18n key

  feedbackControl = new FormControl<string>('', { nonNullable: true });
  publishNameEnControl = new FormControl<string>('', { nonNullable: true });
  publishNameUkControl = new FormControl<string>('', { nonNullable: true });
  publishQuoteEnControl = new FormControl<string>('', { nonNullable: true });
  publishQuoteUkControl = new FormControl<string>('', { nonNullable: true });

  // Labels are eagerly translated (house pattern; they don't react to language switches).
  publishModalButtons: ModalButton[] = [
    { type: 'secondary', text: this.transloco.translate('Cancel'), onClick: () => this.closePublish() },
    { type: 'primary', text: this.transloco.translate('cardsAdminPublish'), onClick: () => this.publish() }
  ];

  private pollSub?: Subscription;
  // Monotonic guard: every state change bumps it; late responses from a
  // superseded request compare and bail (house requestSeq idiom).
  private requestSeq = 0;
  private currentRunId: number | null = null;

  constructor(
    protected cardsAdmin: CardsAdminService,
    protected transloco: TranslocoService,
    protected snackBar: MatSnackBar,
    private destroyRef: DestroyRef
  ) {}

  canGenerate(): boolean {
    return (
      !this.starting() &&
      !!(this.nameEnControl.value.trim() || this.nameUkControl.value.trim()) &&
      !!(this.quoteEnControl.value.trim() || this.quoteUkControl.value.trim()) &&
      !!this.descriptionControl.value.trim()
    );
  }

  generate(): void {
    if (!this.canGenerate()) {
      return;
    }
    const seq = ++this.requestSeq;
    this.starting.set(true);
    this.error.set(null);
    this.cardsAdmin
      .studioGenerate$({
        rarity: this.rarityControl.value,
        nameEn: this.nameEnControl.value.trim(),
        nameUk: this.nameUkControl.value.trim(),
        quoteEn: this.quoteEnControl.value.trim(),
        quoteUk: this.quoteUkControl.value.trim(),
        description: this.descriptionControl.value.trim(),
        hint: this.hintControl.value.trim()
      })
      .subscribe({
        next: (r) => {
          // In-flight flags reset UNCONDITIONALLY - gating them behind the seq check would leave
          // them stuck true when startNew() bumps the seq mid-request. Only visible side effects
          // belong behind the guard.
          this.starting.set(false);
          if (seq !== this.requestSeq) {
            return;
          }
          this.startPolling(r.runId);
        },
        error: (err) => {
          this.starting.set(false);
          if (seq !== this.requestSeq) {
            return;
          }
          if (err?.error?.error === 'duplicateName') {
            this.error.set('cardsAdminStudioDuplicateName');
          }
        }
      });
  }

  protected startPolling(runId: number): void {
    this.requestSeq++;
    this.currentRunId = runId;
    this.run.set(null);
    this.view.set('generating');
    this.pollSub?.unsubscribe();
    this.cardsAdmin
      .getStudioRun$(runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((run) => this.applyRun(run));
    this.pollSub = interval(5000)
      .pipe(
        switchMap(() => this.cardsAdmin.getStudioRun$(runId)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((run) => this.applyRun(run));
  }

  protected applyRun(run: StudioRun): void {
    if (run.id !== this.currentRunId) {
      return; // a late response for a run this tab has already moved past
    }
    this.run.set(run);
    if (run.status === 'generating') {
      return;
    }
    this.pollSub?.unsubscribe();
    if (run.status === 'failed') {
      this.prefillFromRun(run);
      this.view.set('form');
      this.error.set('cardsAdminStudioFailed');
    } else {
      // review (and closed, defensively - e.g. published from the Runs tab)
      this.view.set('review');
    }
  }

  protected prefillFromRun(run: StudioRun): void {
    const s = run.studio;
    if (!s) {
      return;
    }
    this.nameEnControl.setValue(s.name.en);
    this.nameUkControl.setValue(s.name.uk);
    this.quoteEnControl.setValue(s.quote.en);
    this.quoteUkControl.setValue(s.quote.uk);
    this.descriptionControl.setValue(s.description);
    this.hintControl.setValue(s.hint);
    this.rarityControl.setValue(run.rarity);
  }

  ngOnInit(): void {
    // Resume the latest in-flight or reviewable Studio run so a page refresh
    // mid-generation loses nothing. getRuns$ returns newest-first (-id).
    const seq = ++this.requestSeq;
    this.cardsAdmin
      .getRuns$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((runs) => {
        if (seq !== this.requestSeq || this.view() !== 'form') {
          return;
        }
        const resumable = runs.find(
          (r) => r.kind === 'admin' && r.hasStudio && (r.status === 'generating' || r.status === 'review')
        );
        if (resumable) {
          this.startPolling(resumable.id);
        }
      });
  }

  selectedCandidate() {
    const run = this.run();
    const id = this.selectedCandidateId();
    return run?.candidates.find((c) => c.id === id) ?? null;
  }

  selectCandidate(id: number): void {
    this.selectedCandidateId.set(this.selectedCandidateId() === id ? null : id);
  }

  suggestQuotes(): void {
    const name = this.nameEnControl.value.trim() || this.nameUkControl.value.trim();
    const description = this.descriptionControl.value.trim();
    if (!name || !description || this.suggesting()) {
      return;
    }
    const seq = ++this.requestSeq;
    this.suggesting.set(true);
    this.error.set(null);
    this.cardsAdmin.suggestQuotes$(name, description).subscribe({
      next: (suggestions) => {
        this.suggesting.set(false);
        if (seq !== this.requestSeq) {
          return;
        }
        this.suggestions.set(suggestions);
      },
      error: (err) => {
        this.suggesting.set(false);
        if (seq !== this.requestSeq) {
          return;
        }
        if (err?.error?.error === 'aiFailed') {
          this.error.set('cardsAdminStudioAiFailed');
        }
      }
    });
  }

  applySuggestion(s: QuoteSuggestion): void {
    this.quoteEnControl.setValue(s.en);
    this.quoteUkControl.setValue(s.uk);
  }

  stopGeneration(): void {
    // Escape hatch for stuck runs (SIGKILLed worker, wrong idea): flips the
    // run to failed server-side; the poll (plus the immediate fetch below)
    // then routes back to the prefilled form, where Generate is the rerun.
    const runId = this.currentRunId;
    if (runId === null || this.stopping()) {
      return;
    }
    this.stopping.set(true);
    this.cardsAdmin.studioStop$(runId).subscribe({
      next: () => {
        this.stopping.set(false);
        this.cardsAdmin
          .getStudioRun$(runId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((run) => this.applyRun(run));
      },
      error: () => {
        // 409 notGenerating = the run finished or was stopped meanwhile - the
        // poll renders whatever state it actually reached. Other errors
        // already snackbar via the global interceptor.
        this.stopping.set(false);
      }
    });
  }

  iterate(): void {
    const candidateId = this.selectedCandidateId();
    if (candidateId === null || this.starting()) {
      return;
    }
    const seq = ++this.requestSeq;
    this.starting.set(true);
    this.cardsAdmin.studioIterate$(candidateId, this.feedbackControl.value.trim()).subscribe({
      next: (r) => {
        this.starting.set(false);
        if (seq !== this.requestSeq) {
          return;
        }
        this.feedbackControl.setValue('');
        this.startPolling(r.runId);
      },
      error: () => {
        this.starting.set(false);
      }
    });
  }

  openPublish(): void {
    const candidate = this.selectedCandidate();
    if (!candidate) {
      return;
    }
    // Prefill + pristine so `.dirty` marks only admin edits - dirty fields
    // become presence-based overrides (submissions-tab approve idiom).
    this.publishNameEnControl.setValue(candidate.name.en);
    this.publishNameEnControl.markAsPristine();
    this.publishNameUkControl.setValue(candidate.name.uk);
    this.publishNameUkControl.markAsPristine();
    this.publishQuoteEnControl.setValue(candidate.quote.en);
    this.publishQuoteEnControl.markAsPristine();
    this.publishQuoteUkControl.setValue(candidate.quote.uk);
    this.publishQuoteUkControl.markAsPristine();
    this.publishError.set(null);
    this.showPublishModal.set(true);
  }

  closePublish(): void {
    this.requestSeq++;
    this.publishing.set(false);
    this.publishError.set(null);
    this.showPublishModal.set(false);
  }

  publish(): void {
    const run = this.run();
    const candidateId = this.selectedCandidateId();
    if (!run || candidateId === null || this.publishing()) {
      return;
    }
    const overrides: PublishOverrides = {};
    if (this.publishNameEnControl.dirty) {
      overrides.nameEn = this.publishNameEnControl.value.trim();
    }
    if (this.publishNameUkControl.dirty) {
      overrides.nameUk = this.publishNameUkControl.value.trim();
    }
    if (this.publishQuoteEnControl.dirty) {
      overrides.quoteEn = this.publishQuoteEnControl.value.trim();
    }
    if (this.publishQuoteUkControl.dirty) {
      overrides.quoteUk = this.publishQuoteUkControl.value.trim();
    }
    const seq = ++this.requestSeq;
    this.publishing.set(true);
    this.cardsAdmin.publishRun$(run.id, candidateId, overrides).subscribe({
      next: () => {
        this.publishing.set(false);
        if (seq !== this.requestSeq) {
          return;
        }
        this.showPublishModal.set(false);
        this.snackBar.open(this.transloco.translate('cardsAdminPublished'), '', { duration: 3000 });
        this.view.set('published');
      },
      error: (err) => {
        this.publishing.set(false);
        if (seq !== this.requestSeq) {
          return;
        }
        if (err?.error?.error === 'duplicateName') {
          this.publishError.set('cardsAdminStudioDuplicateName');
        } else if (err?.error?.error === 'notPublishable') {
          this.publishError.set('cardsAdminStudioNotPublishable');
        }
      }
    });
  }

  startNew(): void {
    this.requestSeq++;
    this.pollSub?.unsubscribe();
    this.currentRunId = null;
    this.run.set(null);
    this.error.set(null);
    this.suggestions.set([]);
    this.selectedCandidateId.set(null);
    this.showPublishModal.set(false);
    this.publishError.set(null);
    this.starting.set(false);
    this.suggesting.set(false);
    this.publishing.set(false);
    this.stopping.set(false);
    this.feedbackControl.setValue('');
    this.nameEnControl.setValue('');
    this.nameUkControl.setValue('');
    this.quoteEnControl.setValue('');
    this.quoteUkControl.setValue('');
    this.descriptionControl.setValue('');
    this.hintControl.setValue('');
    this.rarityControl.setValue('common');
    this.view.set('form');
  }
}
